import React, { useState, useEffect, useContext, useRef } from "react";
import * as msal from "@azure/msal-browser";
import Auth from "./Auth";

const ua = window.navigator.userAgent;
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;

const auth = new Auth();

export const MsalContext = React.createContext();
export const useMsal = () => useContext(MsalContext);

export const MsalProvider = ({ children, config }) => {
  // Create one PublicClientApplication instance
  const publicClientRef = useRef(null);
  if (!publicClientRef.current) {
    publicClientRef.current = new msal.PublicClientApplication(config);
  }
  const publicClient = publicClientRef.current;

  // Inject shared instance into Auth, so Auth.getToken uses same PublicClientApplication instance
  auth.setClient(publicClient);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userAltocumulus, setUserAltocumulus] = useState();
  const [userAltocumulusAccess, setUserAltocumulusAccess] = useState(false);
  const [, setToken] = useState();
  const [authing, setAuthing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loginError, setLoginError] = useState(false);

  useEffect(() => {
    const existing = publicClient.getAllAccounts();
    if (existing.length > 0) {
      setUser(existing[0]);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(existing[0]));
    }

    setLoading(true);

    publicClient
      .handleRedirectPromise()
      .then((response) => {
        const account =
          response?.account || publicClient.getAllAccounts()[0] || null;

        if (account) {
          setUser(account);
          setIsAuthenticated(true);
          localStorage.setItem("user", JSON.stringify(account));

          // Track login using claims
          auth.trackLogin(account.idTokenClaims);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }

        if (response?.accessToken) {
          setToken(response.accessToken);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoginError(error);
      })
      .finally(() => {
        setLoading(false);
        setAuthing(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = () => {
    const accounts = publicClient.getAllAccounts();
    if (accounts.length > 0) {
      setIsAuthenticated(true);
      setUser(accounts[0]);
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const getTokenPopup = async (loginRequest) => {
    const account = user || publicClient.getAllAccounts()[0];

    const silentRequest = {
      account,
      scopes: loginRequest?.scopes
        ? loginRequest.scopes
        : [process.env.REACT_APP_AZURE_API_SCOPE],
      forceRefresh: false,
    };

    try {
      const response = await publicClient.acquireTokenSilent(silentRequest);
      setToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.log(error);
      try {
        setPopupOpen(true);
        const response = await publicClient.acquireTokenPopup(silentRequest);
        setToken(response.accessToken);
        return response.accessToken;
      } catch (error2) {
        console.log(error2);
        setLoginError(error2);
        return null;
      } finally {
        setPopupOpen(false);
      }
    }
  };

  const getTokenRedirect = async (loginRequest) => {
    const account = user || publicClient.getAllAccounts()[0];

    const request = {
      account,
      scopes: loginRequest?.scopes
        ? loginRequest.scopes
        : [process.env.REACT_APP_AZURE_API_SCOPE],
      forceRefresh: false,
    };

    try {
      const resp = await publicClient.acquireTokenSilent(request);
      setToken(resp.accessToken);
      return resp.accessToken;
    } catch (error) {
      try {
        setLoading(true);
        await publicClient.acquireTokenRedirect(request);
        return null; // redirect navigates away
      } catch (error2) {
        console.log(error2);
        setLoginError(error2);
        return null;
      }
    }
  };

  const getToken = async (loginRequest, method = "loginPopup") => {
    const signInType = isIE || isEdge ? "loginRedirect" : method;
    return signInType === "loginRedirect"
      ? await getTokenRedirect(loginRequest)
      : await getTokenPopup(loginRequest);
  };

  return (
    <MsalContext.Provider
      value={{
        isAuthenticated,
        user,
        userAltocumulus,
        setUserAltocumulus,
        userAltocumulusAccess,
        setUserAltocumulusAccess,
        loading,
        authing,
        popupOpen,
        loginError,
        checkAuth,
        getToken,
      }}
    >
      {children}
    </MsalContext.Provider>
  );
};